import React, { useState } from "react";
import MathRenderer from "../../../../common/MathRenderer";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Bookmark,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Lightbulb,
} from "lucide-react";
import MathRendererContent from "../../../../common/MathRendererContent";
import { Tooltip } from "@mui/material";
import ResultIndicator from "./ResultIndicator";

const ExamQuestion = ({
  id,
  assessmentId,
  questions,
  questionsMQ,
  current,
  currentMQ,
  answers,
  markedQuestions,
  toggleMarkQuestion,
  handleSelect,
  onDragEnd,
  isQuestionAnswered,
  onAnswerChange, // Add this prop for MathRenderer
  savedAnswers, // User answers with isCorrect flag
  correctAnswers, // Correct answers from examData
  isAnswer,
}) => {
  // Helper function to render result indicator for a question
  const renderResultIndicator = (question) => {
    if (!correctAnswers || !question) return null;

    const userAnswer = savedAnswers ? savedAnswers[question.question] : null;
    const correctAnswer = correctAnswers[question.question];

    // Always show if there's a correct answer, regardless of whether user answered
    if (!correctAnswer) return null;

    return (
      <ResultIndicator
        question={question}
        userAnswer={userAnswer}
        correctAnswer={correctAnswer}
        isCorrect={userAnswer ? userAnswer.isCorrect : false}
        isAnswer={isAnswer}
      />
    );
  };

  // Render các lựa chọn đáp án dựa trên loại câu hỏi
  const renderAnswerOptions = (question) => {
    if (!question) {
      console.error("Invalid question object in renderAnswerOptions");
      return null;
    }

    switch (question.type) {
      case "TN":
        // Sử dụng mảng options để hiển thị các đáp án
        const options = ["A", "B", "C", "D"];
        const selectedOption = answers[question.question];

        return (
          <div className="space-y-3 text-base md:text-[1.1rem]">
            {options.map((option) => {
              const isSelected = selectedOption === option;
              if (!question[`contentAnswer${option}`]) return null;
              return (
                <div
                  key={option}
                  className={`flex items-center gap-3 px-4 py-[3px] rounded-lg cursor-pointer transition-all duration-200 ${
                    isSelected ? "" : ""
                  }`}
                  onClick={() => {
                    // Cập nhật trực tiếp state
                    handleSelect(option, "", question);
                  }}
                >
                  <div className="flex-shrink-0 mt-0.5 ">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center ${
                        isSelected
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <span className="leading-relaxed text-gray-800">
                      <MathRenderer
                        content={question[`contentAnswer${option}`]}
                      />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        );

      case "DS":
        return (
          <div className="w-full text-base md:text-[1.1rem]">
            {/* Table Header - No Border */}
            <div className="grid grid-cols-[1fr_80px_80px] p-3 font-medium text-gray-700 mb-1">
              <div />
              <div className="text-center ml-[25px]">Đúng</div>
              <div className="text-center ml-[23px]">Sai</div>
            </div>

            {/* Table Body - With Border */}
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
              {/* Row a */}
              {question.contentYA && (
                <div className="grid grid-cols-[1fr_80px_80px] items-center divide-x divide-gray-200">
                  <div className="py-4 px-4">
                    <MathRenderer content={question.contentYA} />
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ a: "D" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.a === "D"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.a === "D" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ a: "S" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.a === "S"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.a === "S" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Row b */}
              {question.contentYB && (
                <div className="grid grid-cols-[1fr_80px_80px] items-center divide-x divide-gray-200">
                  <div className="py-4 px-4">
                    <MathRenderer content={question.contentYB} />
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ b: "D" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.b === "D"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.b === "D" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ b: "S" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.b === "S"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.b === "S" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Row c */}
              {question.contentYC && (
                <div className="grid grid-cols-[1fr_80px_80px] items-center divide-x divide-gray-200">
                  <div className="py-4 px-4">
                    <MathRenderer content={question.contentYC} />
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ c: "D" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.c === "D"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.c === "D" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ c: "S" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.c === "S"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.c === "S" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Row d */}
              {question.contentYD && (
                <div className="grid grid-cols-[1fr_80px_80px] items-center divide-x divide-gray-200">
                  <div className="py-4 px-4">
                    <MathRenderer content={question.contentYD} />
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ d: "D" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.d === "D"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.d === "D" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ d: "S" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.d === "S"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.d === "S" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Row e */}
              {question.contentYE && (
                <div className="grid grid-cols-[1fr_80px_80px] items-center divide-x divide-gray-200">
                  <div className="py-4 px-4">
                    <MathRenderer content={question.contentYE} />
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ e: "D" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.e === "D"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.d === "D" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ e: "S" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.e === "S"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.d === "S" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Row f */}
              {question.contentYF && (
                <div className="grid grid-cols-[1fr_80px_80px] items-center divide-x divide-gray-200">
                  <div className="py-4 px-4">
                    <MathRenderer content={question.contentYF} />
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ f: "D" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.f === "D"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.d === "D" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                  <div className="flex justify-center h-full items-center">
                    <button
                      onClick={() => handleSelect({ f: "S" }, "", question)}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers[question?.question]?.f === "S"
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.d === "S" && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case "MA":
        return (
          <div className="space-y-4 text-base md:text-[1.1rem]">
            {/* Options */}
            {[
              "contentC1",
              "contentC2",
              "contentC3",
              "contentC4",
              "contentC5",
              "contentC6",
            ].map((contentKey, index) => {
              const optionKey = `c${index + 1}`;
              if (!question[contentKey]) return null;

              return (
                <div
                  key={optionKey}
                  className="flex items-center gap-3 px-4 py-[3px] transition-all duration-200 rounded-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(optionKey, "", question);
                  }}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div
                      className={`w-8 h-8 rounded border-2 flex items-center justify-center transition-colors ${
                        answers[question?.question]?.includes(optionKey)
                          ? "border-blue-500 bg-blue-500"
                          : "bg-[#e8e8e8]"
                      }`}
                    >
                      {answers[question?.question]?.includes(optionKey) && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <span className="leading-relaxed text-gray-800">
                      <MathRenderer content={question[contentKey]} />
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={answers[question?.question]?.includes(optionKey)}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelect(optionKey, "", question);
                    }}
                  />
                </div>
              );
            })}
          </div>
        );

      case "TLN":
        return null;
      // <div className="w-full">
      //   {/* Input Section - Ngắn gọn và đầu dòng */}
      //   <div className="mb-4">
      //     <div className="relative w-[17rem]">
      //       <input
      //         type="text"
      //         value={answers[question?.question] ?? ""}
      //         onChange={(e) => handleSelect(e.target.value, "", question)}
      //         placeholder="Nhập câu trả lời..."
      //         className="w-full p-4 text-sm text-gray-800 placeholder-gray-400 transition-all duration-200 bg-white border-2 border-gray-300 rounded-lg shadow-sm md:text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 hover:border-gray-400"
      //       />
      //       <div className="absolute inset-y-0 right-0 flex items-center pr-3">
      //         <svg
      //           className="w-5 h-5 text-gray-400"
      //           fill="none"
      //           stroke="currentColor"
      //           viewBox="0 0 24 24"
      //         >
      //           <path
      //             strokeLinecap="round"
      //             strokeLinejoin="round"
      //             strokeWidth="2"
      //             d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      //           />
      //         </svg>
      //       </div>
      //     </div>
      //   </div>

      //   {/* Hint Section */}
      //   {question.hint && (
      //     <div className="p-3 border-l-4 border-blue-400 rounded-r-lg bg-blue-50">
      //       <div className="flex items-start">
      //         <span className="text-blue-500 mr-2">💡</span>
      //         <div className="text-sm text-blue-700">
      //           <MathRenderer content={question.hint} />
      //         </div>
      //       </div>
      //     </div>
      //   )}
      // </div>

      case "TLN_M":
        // const tlnmAnswers = answers[question?.question] || {};
        // const subQuestions = Object.keys(tlnmAnswers)
        //   .map((k, index) => ({
        //     key: k,
        //     content: question[`contentY${index + 1}`],
        //   }))
        //   .filter((sub) => sub.content);
        // return (
        //   <div className="w-full space-y-3">
        //     {subQuestions.map((sub, idx) => (
        //       <div key={sub.key} className="flex items-center gap-2">
        //         {/* Mệnh đề */}
        //         <div className="text-sm font-semibold text-gray-800 md:text-base">
        //           {sub.key}
        //         </div>

        //         {/* Nội dung mệnh đề và Input inline */}
        //         <div className="flex flex-wrap items-center flex-1 gap-2">
        //           <div className="text-sm text-gray-800 md:text-base">
        //             <MathRenderer content={sub.content} />
        //           </div>

        //           {/* Input ngay sau nội dung mệnh đề */}
        //           <div className="relative w-[17rem]">
        //             <input
        //               type="text"
        //               value={tlnmAnswers[sub.key] ?? ""}
        //               onChange={(e) =>
        //                 handleSelect(e.target.value, sub, question)
        //               }
        //               placeholder="Nhập câu trả lời..."
        //               className="w-full p-4 text-sm text-gray-800 placeholder-gray-400 transition-all duration-200 bg-white border-2 border-gray-300 rounded-lg shadow-sm md:text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 hover:border-gray-400"
        //             />
        //             <div className="absolute inset-y-0 right-0 flex items-center pr-3">
        //               <svg
        //                 className="w-5 h-5 text-gray-400"
        //                 fill="none"
        //                 stroke="currentColor"
        //                 viewBox="0 0 24 24"
        //               >
        //                 <path
        //                   strokeLinecap="round"
        //                   strokeLinejoin="round"
        //                   strokeWidth="2"
        //                   d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
        //                 />
        //               </svg>
        //             </div>
        //           </div>
        //         </div>
        //       </div>
        //     ))}

        //     {/* Hint Section */}
        //     {question.hint && (
        //       <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg mt-4">
        //         <div className="flex items-start">
        //           <span className="mr-2 text-blue-500">💡</span>
        //           <div className="text-sm text-blue-700">
        //             <MathRenderer content={question.hint} />
        //           </div>
        //         </div>
        //       </div>
        //     )}
        //   </div>
        // );
        return null;

      case "KT":
        // const currentAnswers = answers[question?.question] || {};
        // const subQuestions_KT = Object.keys(currentAnswers)
        //   .map((k, index) => ({
        //     slotKey: k,
        //     content: question[`contentY${index + 1}`],
        //   }))
        //   .filter((sub) => sub.content);

        // // Get items that are not currently in any slot
        // const availableItems = question.items.filter(
        //   (item) => !Object.values(currentAnswers).includes(item.id)
        // );

        // return (
        //   <DragDropContext onDragEnd={onDragEnd(question)}>
        //     <div className="w-full space-y-6">
        //       {/* Items to drag */}
        //       <Droppable droppableId="items" direction="horizontal">
        //         {(provided) => (
        //           <div
        //             ref={provided.innerRef}
        //             {...provided.droppableProps}
        //             className="flex flex-wrap gap-3 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300"
        //           >
        //             {availableItems.map((item, index) => (
        //               <Draggable
        //                 key={item.id}
        //                 draggableId={item.id}
        //                 index={index}
        //               >
        //                 {(provided, snapshot) => (
        //                   <div
        //                     ref={provided.innerRef}
        //                     {...provided.draggableProps}
        //                     {...provided.dragHandleProps}
        //                     className={`px-4 py-2 bg-white rounded-lg border-2 ${
        //                       snapshot.isDragging
        //                         ? "border-blue-500 shadow-lg"
        //                         : "border-gray-200 hover:border-blue-400"
        //                     } cursor-grab transition-all duration-200`}
        //                   >
        //                     <MathRenderer content={item.content} />
        //                   </div>
        //                 )}
        //               </Draggable>
        //             ))}
        //             {provided.placeholder}
        //           </div>
        //         )}
        //       </Droppable>

        //       {/* Drop zones */}
        //       <div className="space-y-4">
        //         {subQuestions_KT.map((sub, index) => {
        //           return (
        //             <Droppable key={sub?.slotKey} droppableId={sub?.slotKey}>
        //               {(provided, snapshot) => (
        //                 <div className="flex items-center gap-2">
        //                   {/* Số thứ tự mệnh đề */}
        //                   <div className="text-gray-800 text-sm md:text-base font-semibold">
        //                     {index + 1})
        //                   </div>

        //                   {/* Nội dung mệnh đề và Drop zone inline */}
        //                   <div className="flex-1 flex items-center gap-2 flex-wrap">
        //                     {/* Nội dung mệnh đề */}
        //                     {sub?.content && (
        //                       <div className="text-gray-800 text-sm md:text-base">
        //                         <MathRenderer content={sub?.content} />
        //                       </div>
        //                     )}

        //                     {/* Drop zone ngay sau nội dung mệnh đề */}
        //                     <div
        //                       ref={provided.innerRef}
        //                       {...provided.droppableProps}
        //                       className={`w-[17rem] p-4 border-2 rounded-lg flex items-center transition-colors ${
        //                         snapshot.isDraggingOver
        //                           ? "border-blue-500 bg-blue-50"
        //                           : "border-gray-300 bg-white"
        //                       }`}
        //                     >
        //                       {currentAnswers[sub?.slotKey] ? (
        //                         <Draggable
        //                           draggableId={currentAnswers[sub?.slotKey]}
        //                           index={0}
        //                         >
        //                           {(provided, snapshot) => (
        //                             <div
        //                               ref={provided.innerRef}
        //                               {...provided.draggableProps}
        //                               {...provided.dragHandleProps}
        //                               className={`w-full ${
        //                                 snapshot.isDragging ? "opacity-50" : ""
        //                               }`}
        //                             >
        //                               <MathRenderer
        //                                 content={
        //                                   question.items.find(
        //                                     (item) =>
        //                                       item.id ===
        //                                       currentAnswers[sub?.slotKey]
        //                                   )?.content || ""
        //                                 }
        //                               />
        //                             </div>
        //                           )}
        //                         </Draggable>
        //                       ) : (
        //                         <div className="text-gray-400 italic text-sm">
        //                           Kéo thả vào đây
        //                         </div>
        //                       )}
        //                       {provided.placeholder}
        //                     </div>
        //                   </div>
        //                 </div>
        //               )}
        //             </Droppable>
        //           );
        //         })}
        //       </div>

        //       {/* Hint Section */}
        //       {question.hint && (
        //         <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg mt-4">
        //           <div className="flex items-start">
        //             <span className="text-blue-500 mr-2">💡</span>
        //             <div className="text-sm text-blue-700">
        //               <MathRenderer content={question.hint} />
        //             </div>
        //           </div>
        //         </div>
        //       )}
        //     </div>
        //   </DragDropContext>
        // );
        return null;
      default:
        return <div>Unsupported question type</div>;
    }
  };

  // Component hiển thị lời giải
  const ExplanationSection = ({ explanation, questionNumber }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!explanation) return null;

    const toggleExpansion = () => {
      setIsExpanded(!isExpanded);
    };

    return (
      <div className="mt-6">
        <button
          onClick={toggleExpansion}
          className="w-full flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 text-left bg-gradient-to-r from-emerald-50 via-blue-50 to-purple-50 hover:from-emerald-100 hover:via-blue-100 hover:to-purple-100 transition-all duration-300 rounded-xl border-2 border-transparent shadow-sm hover:shadow-md group"
          style={{
            background: isExpanded
              ? "linear-gradient(135deg, #f0fdfa 0%, #eff6ff 50%, #faf5ff 100%)"
              : "linear-gradient(135deg, #f0fdf4 0%, #f0f9ff 50%, #fdf4ff 100%)",
          }}
        >
          <div className="flex items-center gap-3 sm:gap-4">
            <div
              className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-xl transition-all duration-300 ${
                isExpanded
                  ? "bg-gradient-to-br from-emerald-500 to-blue-600 shadow-lg"
                  : "bg-gradient-to-br from-blue-500 to-purple-600 group-hover:shadow-md"
              }`}
            >
              <Lightbulb size={16} className="text-white sm:hidden" />
              <Lightbulb size={18} className="text-white hidden sm:block" />
            </div>
            <div>
              <span className="font-semibold text-gray-800 text-sm sm:text-base">
                💡 Lời giải chi tiết
              </span>
              {questionNumber && (
                <div className="text-xs sm:text-sm text-gray-600 mt-0.5">
                  Câu {questionNumber}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <span
              className={`hidden sm:inline text-sm font-medium transition-colors duration-200 ${
                isExpanded ? "text-emerald-600" : "text-blue-600"
              }`}
            >
              {isExpanded ? "Thu gọn" : "Xem lời giải"}
            </span>
            <div
              className={`p-1 sm:p-1.5 rounded-full transition-all duration-300 ${
                isExpanded
                  ? "bg-emerald-100 text-emerald-600"
                  : "bg-blue-100 text-blue-600 group-hover:bg-blue-200"
              }`}
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </div>
          </div>
        </button>

        <div
          className={`transition-all duration-500 ease-out overflow-hidden ${
            isExpanded
              ? "max-h-[3000px] opacity-100 transform translate-y-0"
              : "max-h-0 opacity-0 transform -translate-y-2"
          }`}
        >
          {isExpanded && (
            <div className="mt-4 p-6 bg-gradient-to-br from-white via-gray-50 to-blue-50 border border-gray-200 rounded-xl shadow-lg">
              {/* Header với icon và tiêu đề */}
              <div className="flex items-center gap-3 mb-6 pb-4 relative">
                <div className="flex items-center justify-center w-9 h-9 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-xl shadow-md">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-bold text-xl bg-gradient-to-r from-emerald-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                  📚 Hướng dẫn giải
                </h4>
                <div className="flex-1 h-px bg-gradient-to-r from-emerald-300 via-blue-300 to-purple-300 rounded-full"></div>

                {/* Decorative border */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400 rounded-full"></div>
              </div>

              {/* Nội dung lời giải */}
              <div className="explanation-content bg-white p-6 rounded-xl border border-gray-100 shadow-sm relative overflow-hidden">
                {/* Decorative background pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-30 -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-emerald-100 to-blue-100 rounded-full opacity-30 translate-y-12 -translate-x-12"></div>

                {/* Content wrapper */}
                <div className="relative z-10">
                  <div
                    className="prose prose-sm max-w-none text-gray-700 text-base md:text-[1.1rem]"
                    style={{
                      lineHeight: "1.7",
                      fontSize: "15px",
                    }}
                  >
                    <MathRendererContent content={explanation} />
                  </div>
                </div>
              </div>

              {/* Footer với ghi chú */}
              <div className="mt-6 pt-4 border-t border-gradient-to-r from-emerald-200 to-blue-200 flex items-center justify-center relative">
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-full border border-emerald-200">
                  <span className="text-xs font-medium bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
                    ✨ PIMAX Solution
                  </span>
                  <div className="w-1 h-1 bg-gradient-to-r from-emerald-400 to-blue-400 rounded-full"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMQQuestion = (question) => {
    return (
      <div className="flex flex-col lg:flex-row flex-1 h-full overflow-hidden rounded-lg shadow-sm bg-white border border-gray-200">
        {/* Left: Parent Question */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-white border-r border-gray-300">
          <div className="flex items-start justify-between">
            <h2 className="text-xl font-bold mb-4">{question.title}</h2>
          </div>

          <p className="text-gray-700 mb-4 text-base md:text-[1.1rem]">
            <MathRendererContent content={question?.contentQuestions} />
          </p>
          {question.imageUrl && (
            <img
              src={question.imageUrl}
              alt="Question"
              className="max-w-full h-auto mx-auto"
            />
          )}
        </div>

        {/* Right: Child Questions */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-white border-t-4 border-gray-200 lg:border-none">
          {question.range.map((num) => {
            const childQuestion = questions.find(
              (q) =>
                q.question === `Câu ${num}` || q.question === `Câu hỏi ${num}`
            );
            const childQuestionIndex = questions.findIndex(
              (q) =>
                q.question === `Câu ${num}` || q.question === `Câu hỏi ${num}`
            );
            return (
              <div
                key={num}
                className="mb-6 border-gray-200"
                id={`question-${childQuestionIndex}`}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-[#f2f2f2] text-black rounded-full text-lg font-bold">
                      {num}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-800 text-base md:text-[1.1rem] leading-relaxed">
                      <MathRenderer
                        content={childQuestion?.contentQuestions}
                        questionId={childQuestion.question}
                        id={id}
                        assessmentId={assessmentId}
                        type={childQuestion.type}
                        availableItems={childQuestion?.items}
                        answers={answers}
                        onAnswerChange={onAnswerChange}
                      />
                    </p>
                    {childQuestion?.imageUrl && (
                      <img
                        src={childQuestion.imageUrl}
                        alt="Question"
                        className="mt-4 max-w-full h-auto mx-auto"
                      />
                    )}
                  </div>

                  {/* Icon đánh dấu câu hỏi con ở góc phải */}
                  <Tooltip
                    title={
                      markedQuestions.includes(childQuestionIndex)
                        ? "Bỏ đánh dấu"
                        : "Đánh dấu câu chưa chắc chắn"
                    }
                    placement="top"
                  >
                    <button
                      className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
                        markedQuestions.includes(childQuestionIndex)
                          ? "text-[#f59e0b] border-[#f59e0b] hover:text-[#f59e0b]"
                          : "bg-white text-gray-500 border-gray-300 hover:border-[#f59e0b] hover:text-[#f59e0b]"
                      }`}
                      onClick={() => toggleMarkQuestion(childQuestionIndex)}
                    >
                      <Bookmark />
                    </button>
                  </Tooltip>
                </div>
                <div className="space-y-3">
                  {childQuestion && renderAnswerOptions(childQuestion)}
                </div>

                {/* Hiển thị kết quả cho câu hỏi con */}
                {childQuestion && renderResultIndicator(childQuestion)}

                {/* Thêm lời giải cho câu hỏi con */}
                {isAnswer && childQuestion && childQuestion.explanation && (
                  <ExplanationSection
                    explanation={childQuestion.explanation}
                    questionNumber={num}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Thêm lời giải cho câu hỏi chính */}
        {question && question.explanation && (
          <div className="p-4 md:p-6 border-t border-gray-200">
            <ExplanationSection
              explanation={question.explanation}
              questionNumber={question.title}
            />
          </div>
        )}
      </div>
    );
  };

  const renderQuestion = () => {
    const currentQuestion = questions[current];
    if (!currentQuestion) return null;

    // Lấy số thứ tự câu hỏi thực tế
    const currentQuestionNumber = parseInt(
      currentQuestion.question.match(/\d+/)?.[0] || "0"
    );

    // Kiểm tra xem câu hỏi hiện tại có thuộc MQ group nào không
    const currentQuestionMQ = questionsMQ.find((e) =>
      e?.range.includes(currentQuestionNumber)
    );

    if (currentQuestionMQ) {
      return renderMQQuestion(currentQuestionMQ);
    }

    return (
      <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 overflow-y-auto max-h-[calc(100vh-200px)]">
        <div className="flex items-start gap-4 mb-6">
          <div className="flex-shrink-0">
            <span className="inline-flex items-center justify-center w-10 h-10 bg-[#f2f2f2] text-black-500 rounded-full text-lg font-semibold">
              {current + 1}
            </span>
          </div>
          <div className="flex-1">
            <p className="text-gray-800 text-base md:text-[1.1rem] leading-relaxed">
              <MathRenderer
                content={currentQuestion?.contentQuestions}
                questionId={currentQuestion.question}
                id={id}
                assessmentId={assessmentId}
                type={currentQuestion.type}
                availableItems={currentQuestion?.items}
                answers={answers}
                onAnswerChange={onAnswerChange}
              />
            </p>
            {currentQuestion?.imageUrl && (
              <img
                src={currentQuestion?.imageUrl}
                alt="Question"
                className="mt-4 max-w-full h-auto mx-auto"
              />
            )}
          </div>

          {/* Icon đánh dấu câu hỏi ở góc phải */}
          <button
            className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
              markedQuestions.includes(current)
                ? " text-[#f59e0b] border-[#f59e0b] hover:text-[#f59e0b]"
                : "bg-white text-gray-500 border-gray-300 hover:border-[#f59e0b] hover:text-[#f59e0b]"
            }`}
            onClick={() => toggleMarkQuestion(current)}
            title={
              markedQuestions.includes(current)
                ? "Bỏ đánh dấu"
                : "Đánh dấu làm sau"
            }
          >
            <Bookmark />
          </button>
        </div>

        {/* Answer Options based on question type */}
        <div className="space-y-3">
          {currentQuestion && renderAnswerOptions(currentQuestion)}
        </div>

        {/* Hiển thị kết quả cho câu hỏi */}
        {currentQuestion && renderResultIndicator(currentQuestion)}

        {/* Thêm lời giải cho câu hỏi */}
        {isAnswer && currentQuestion && currentQuestion.explanation && (
          <ExplanationSection
            explanation={currentQuestion.explanation}
            questionNumber={current + 1}
          />
        )}
      </div>
    );
  };

  return renderQuestion();
};

export default ExamQuestion;
